import React from 'react';
import { IStepModuleProps, ModuleSearchParam } from 'src/models/modules';
import { StepType } from 'src/services/getStepOrder';
import { useIntl } from 'react-intl';
import {
    useSchedules,
    handleEventRender,
    handleEventClassNames,
    IEventRenderInfo,
} from 'src/components/BookingFlow/modules/TimeTableSelect/fullcalendarFunctions';
import { IServiceDetails } from 'src/entities/service';
import { ISchedule } from 'src/entities/schedule';
import { useSubscription } from 'src/hooks';
import { getCalendarFirstFreeSlot } from 'src/services/api/calendar';
import { lazy, Suspense } from 'react';
import Placeholder from 'src/components/Placeholder/Placeholder';
import withSearchParams, { getDateParam, ISearchParamsOptions } from '../withSearchParams';
import AvailabilityWarning from 'src/components/AvailabilityWarning';
import { ReadonlyInfo } from 'src/components/ReadonlyInfo/ReadonlyInfo';

const Timetable = lazy(
    () => import(/* webpackChunkName: "Timetable" */ '../DateTimeSelect/Timetable')
);

interface ITimeTableSelectProps extends IStepModuleProps {
    isPaymentEnabled?: boolean;
}

const TimeTableSelect: React.FunctionComponent<ITimeTableSelectProps> = ({
    data,
    onComplete,
    isPaymentEnabled,
}) => {
    const calendar = data[StepType.Calendar]!;

    const intl = useIntl();
    const { getSchedules, scheduleLists, isLoading } = useSchedules(calendar);
    const fetchFirstSlot = React.useCallback(
        () => getCalendarFirstFreeSlot(calendar.Id),
        [calendar.Id]
    );
    const [firstFreeSlot, isFirstFreeSlotLoading] = useSubscription(fetchFirstSlot, '');
    const userViewSlotMinutes = calendar.UserViewSlotMinutes;

    const handleServiceSelect = (
        service: IServiceDetails,
        schedule: ISchedule,
        personCount: number
    ) => {
        onComplete({
            ...data,
            [StepType.Timetable]: {
                service,
                schedule,
                personCount,
                displayValue: service.Name,
            },
        });
    };

    const eventRender = (arg: IEventRenderInfo & { currentWeekDay: number | false }) =>
        handleEventRender({
            ...arg,
            intl,
            handleServiceSelect,
            currentWeekDay: arg.currentWeekDay,
            minTimeReserve: calendar.MinTimeReserve,
            isReadonly: calendar.Readonly,
            hideSeatCount: calendar.HideSeatCount,
            isWeekendDisabled: calendar.WeekendDisabled,
            isPaymentEnabled,
            firstFreeSlot
        });

    const eventClassNames = (arg: IEventRenderInfo) =>
        handleEventClassNames({
            ...arg,
            minTimeReserve: calendar.MinTimeReserve,
            firstFreeSlot
        });

    if (isFirstFreeSlotLoading) {
        return <Placeholder />;
    }

    if (!firstFreeSlot) {
        return <AvailabilityWarning type="noFreeIntervals" modifier="center" />;
    }

    return (
        <Suspense fallback={<Placeholder />}>
            {calendar.Readonly && (
                <ReadonlyInfo
                    phoneNumber={calendar.PhoneNumber}
                    className="readonly--booking-step"
                />
            )}
            <Timetable
                {...{
                    key: calendar.Id,
                    calendar,
                    getEventTimes: getSchedules,
                    handleEventRender: eventRender,
                    handleEventClassNames: eventClassNames,
                    eventTimes: scheduleLists,
                    slotDuration: userViewSlotMinutes,
                    areEventsLoading: isLoading,
                    firstFreeSlot,
                    querySelectedDate: getDateParam(data.searchParams),
                    userViewSlotMinutes,
                }}
            />
        </Suspense>
    );
};

const searchParamsOptions: ISearchParamsOptions<string> = {
    paramName: ModuleSearchParam.Date
};

export default withSearchParams<ITimeTableSelectProps, any>(TimeTableSelect, searchParamsOptions);